import type { InstalledExtension, Prompt } from "@1js/pl-types";
import { isUserCreatedExtensionPrompt } from "@1js/pl-utils";
import { useMsal } from "@azure/msal-react";
import { makeStyles, shorthands, tokens } from "@fluentui/react-components";
import * as React from "react";
import { Backdrop } from "../../components/Backdrop/Backdrop.web";
import { Faqs as FaqSection } from "../../components/Faqs/Faqs.web";
import type { FaqsProps } from "../../components/Faqs/Faqs.web.data";
import type { AppLinkType } from "../../components/PromptHeading/PromptHeading.web";
import { PromptHeading as HeadingSection } from "../../components/PromptHeading/PromptHeading.web";
import { Tips as TipSection } from "../../components/Tips/Tips.web";
import type { TipCardsProps } from "../../components/Tips/Tips.web.data";
import { useFeatureEnablement } from "../../hooks/useFeatureEnablement";
import { useFlightContext } from "../../hooks/useFlightContext";
import {
  fetchAllGroupPrompts,
  fetchAllPrompts,
  fetchGptPrompts,
  fetchSavedPrompts,
} from "../../services/prompt-client.web";
import { fetchInstalledExtensions } from "../../services/usercopilot-extensions-client";
import { sendDiagnosticEvent } from "../../utils/Telemetry";
import { LogCategory, LogType } from "../../utils/Telemetry/events";
import useTelemetry from "../../utils/Telemetry/useTelemetry";
import { MetaOSAppContext } from "../metaos/MetaOSAppContext";
import { PropertyContext } from "../PropertyContext.web";
import { AllPromptsLibrary as AllPromptsSection } from "./allPromptsLibrary/AllPromptsLibrary.web";
import usePrompts from "./usePrompts.web";
export type PromptsPageContentProps = {
  faqs: FaqsProps;
  tips: TipCardsProps;
  locale: string;
  serviceHost: string;
};

const useStyles = makeStyles({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: tokens.colorNeutralBackground2,
    backgroundSize: "cover",
    ...shorthands.gap("1.5rem"),
    ...shorthands.padding("0px", "0px", "56px", "0px"),
    "@media (max-width: 540px)": {
      ...shorthands.padding("0px", "0px", "40px", "0px"),
    },
  },

  promptLib: {
    minHeight: "175px",
  },

  promptLibLoading: {
    minHeight: "1000px",
  },
});

export const PromptsPageContent: React.FC<PromptsPageContentProps> = ({
  faqs,
  tips,
  serviceHost,
  locale,
}) => {
  const telemetry = useTelemetry();
  const styles = useStyles();
  const [promptSettings] = React.useContext(PropertyContext);
  const { state, dispatch } = usePrompts();
  const { isFlightActive } = useFlightContext();
  const metaOSAppContext = React.useContext(MetaOSAppContext);
  const msalInstance = useMsal();
  const instance =
    metaOSAppContext.isMosApp && metaOSAppContext.pca
      ? msalInstance.instance
      : undefined;
  const { isShowAppLinkEnabledSignedInUser, isShowAppLinkEnabledNotSignedIn } =
    useFeatureEnablement();
  const [showAppLink, setShowAppLink] = React.useState<AppLinkType>("None");

  const fetchPromptsAsync = React.useCallback(async () => {
    if (!state.init) {
      try {
        const startTimer = Date.now();
        dispatch({ type: "REQUEST_ALL_PROMPTS_INIT" });
        const requests: Promise<Prompt[]>[] = [];

        let is3SServiceEnabled = false;

        let installedGpts: InstalledExtension[] = [];
        let extensionIconMap: Record<string, string> = {};
        try {
          if (
            isFlightActive("isDeclarativeCopilotPromptEnabled") ||
            isFlightActive("isPluginPromptEnabled")
          ) {
            const installedExtensions = await fetchInstalledExtensions(
              serviceHost,
              locale,
              telemetry.sendApiActionEvent
            );

            extensionIconMap = installedExtensions.reduce(
              (acc, extension) => {
                if (extension.providerId) {
                  acc[extension.providerId] = extension.iconUrl;
                }
                return acc;
              },
              {} as Record<string, string>
            );

            installedGpts = installedExtensions.filter(
              (extension) => extension.type === "GPT"
            );
          }
        } catch (error) {
          console.error("Error fetching installed extensions:", error);
        }

        // Microsoft created and plugin prompts
        requests.push(
          fetchAllPrompts(
            serviceHost,
            locale,
            telemetry.sendApiActionEvent,
            isFlightActive("is3SServiceEnabled"),
            instance
          ).then(([prompts, _is3SServiceEnabled]) => {
            is3SServiceEnabled = _is3SServiceEnabled;
            return prompts;
          })
        );

        // User created prompts
        if (isFlightActive("isPromptSavingEnabled") && promptSettings.user) {
          requests.push(
            fetchSavedPrompts(
              serviceHost,
              promptSettings.user,
              telemetry.sendApiActionEvent
            )
          );
        }

        // Gpt prompts
        const gptPromptRequests: Promise<Prompt[]>[] = [];
        if (
          isFlightActive("isDeclarativeCopilotPromptEnabled") &&
          installedGpts.length > 0
        ) {
          installedGpts.forEach((gpt) => {
            if (gpt.providerId) {
              gptPromptRequests.push(
                fetchGptPrompts(
                  serviceHost,
                  locale,
                  gpt.providerId,
                  telemetry.sendApiActionEvent
                )
              );
            }
          });
        }

        // Team prompts
        if (isFlightActive("isUnifiedWorkgroupPromptsViewEnabled")) {
          requests.push(
            fetchAllGroupPrompts(
              serviceHost,
              locale,
              telemetry.sendApiActionEvent,
              instance
            )
          );
        }

        // resolve all requests
        let prompts = (await Promise.all(requests)).flatMap(
          (prompts) => prompts
        );

        //resolve all gpt prompts
        try {
          if (gptPromptRequests.length > 0) {
            const gptPrompts = (await Promise.all(gptPromptRequests)).flatMap(
              (prompts) => prompts
            );
            prompts.push(...gptPrompts);
          }
        } catch (error) {
          console.error("Error fetching gpt prompts", error);
        }

        //Filter out all plugin prompts if flag is false
        if (!isFlightActive("isPluginPromptEnabled")) {
          prompts = prompts.filter((prompt) => prompt.Origin != "Plugin");
        }

        //Filter out all user agent prompts if flags are false
        if (
          !isFlightActive("isDeclarativeCopilotPromptEnabled") ||
          !isFlightActive("isAgentPromptSavingEnabled")
        ) {
          prompts = prompts.filter(
            (prompt) => !isUserCreatedExtensionPrompt(prompt)
          );
        }

        dispatch({
          type: "REQUEST_ALL_PROMPTS_SUCCEED",
          payload: { prompts: [...prompts], extensionIconMap },
        });

        telemetry.sendResultsRenderedEvent(
          Date.now() - startTimer,
          is3SServiceEnabled,
          prompts,
          instance
        );
      } catch (error) {
        console.error("Error fetching more prompts:", error);
        dispatch({ type: "REQUEST_ALL_PROMPTS_FAILED" });
      }
    }
    // disable exhaustive deps because we don't want to refetch prompts when promptSettings changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.init]);

  React.useEffect(() => {
    if (isShowAppLinkEnabledSignedInUser) {
      setShowAppLink("SignedIn");
      sendDiagnosticEvent(
        LogType.Informational,
        "showAppLinkSignedIn",
        LogCategory.PROMPTS
      );
    } else if (isShowAppLinkEnabledNotSignedIn) {
      setShowAppLink("NotSignedIn");
      sendDiagnosticEvent(
        LogType.Informational,
        "showAppLinkNotSignedIn",
        LogCategory.PROMPTS
      );
    }
  }, [
    setShowAppLink,
    isShowAppLinkEnabledSignedInUser,
    isShowAppLinkEnabledNotSignedIn,
  ]);

  React.useEffect(() => {
    fetchPromptsAsync();

    sendDiagnosticEvent(
      LogType.Informational,
      `Website all prompts page loaded`,
      LogCategory.PROMPTS
    );
  }, [fetchPromptsAsync]);

  return (
    <>
      {!isFlightActive("isUserLicensed") && <Backdrop />}
      <div className={styles.main} id="content">
        <HeadingSection showAppLink={showAppLink} />
        <AllPromptsSection
          status={state.status}
          prompts={state.allPrompts}
          serviceHost={serviceHost}
          locale={locale}
          isTeachingExperiencesEnabled={true}
          extensionIconMap={state.extensionIconMap}
          isRedirectToTabs={false}
          isMetaOsV2HomePage={false}
          isMetaOsV2={false}
        />
        <TipSection {...tips} />
        <FaqSection {...faqs} />
      </div>
    </>
  );
};
